import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellValueChangedEvent,
  FillOperationParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  ValueGetterParams,
  ValueSetterParams,
} from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import { isEqual, isString } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, ReplaySubject } from 'rxjs';

import { AgActionsComponent } from '@shared/ag-components/ag-actions/ag-actions.component';
import { AgCellWrapperComponent } from '@shared/ag-components/ag-cell-wrapper/ag-cell-wrapper.component';
import { AgDatePickerComponent } from '@shared/ag-components/ag-date-picker/ag-date-picker.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { TableConstants } from '@shared/constants/table.constants';
import { CreatePaymentMilestoneInput, EventType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { OrganizationService } from '@shared/store/organization/organization.service';
import { OrganizationModel } from '@shared/store/organization/organization.store';
import { Option } from '@shared/types/components.type';
import { AgSetColumnsVisible } from '@shared/utils';
import { agFormatToNumberValueSetter } from '@shared/utils/ag-value-setter.utils';
import { RequireSome, Utils } from '@shared/utils/utils';

import { PaymentMilestoneQuery } from './state/payment-milestone.query';
import { PaymentMilestoneService } from './state/payment-milestone.service';
import { PaymentMilestoneStore } from './state/payment-milestone.store';

interface PaymentMilestoneGridData {
  amount?: number | undefined;
  id: string;
  name?: string | undefined;
  organization: null | OrganizationModel;
  target_date?: string | undefined;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, AgGridAngular, AsyncPipe],
  selector: 'aux-payment-milestones',
  styles: [
    `
      ::ng-deep #paymentMilestonesGrid .has-error .ag-cell {
        background-color: #fee2e2 !important;
      }
    `,
  ],
  templateUrl: './payment-milestones.component.html',
})
export class PaymentMilestonesComponent {
  organizationQuery = inject(OrganizationQuery);
  paymentMilestoneQuery = inject(PaymentMilestoneQuery);

  private eventService = inject(EventService);
  private mainQuery = inject(MainQuery);
  private organizationService = inject(OrganizationService);
  private overlayService = inject(OverlayService);
  private paymentMilestoneService = inject(PaymentMilestoneService);
  private paymentMilestoneStore = inject(PaymentMilestoneStore);

  editedRows = new Set<string>();
  editModeGrid$ = new BehaviorSubject(false);
  gridAPI!: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  gridData$: Observable<PaymentMilestoneGridData[]> =
    this.paymentMilestoneQuery.selectAllMilestones();

  private vendorValueSetter = (params: ValueSetterParams) => {
    const newValue = isString(params.newValue)
      ? this.vendorOptions().find(({ label }) => label === params.newValue)
      : params.newValue;

    params.data.organization = {
      ...params.data.organization,
      id: newValue.value,
      name: newValue.label,
    };

    return true;
  };

  gridOptions = {
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      cellRenderer: AgCellWrapperComponent,
      editable: () => {
        return this.editModeGrid$.getValue();
      },
    },
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    columnDefs: [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
      },
      {
        field: 'randomID',
        headerName: 'RANDOM ID',
        hide: true,
      },
      {
        ...TableConstants.DEFAULT_GRID_OPTIONS.ACTIONS_COL_DEF,
        cellRenderer: AgActionsComponent,
        cellRendererParams: {
          deleteClickFN: ({ rowNode }: { rowNode: IRowNode }) => {
            if (rowNode.data.id) {
              this.removedRows.push(rowNode.data.id);
            }

            this.gridAPI.applyTransaction({ remove: [rowNode.data] });
            this.checkChanges();
          },
        },
        hide: true,
      },
      {
        cellClass: 'text-left',
        field: 'name',
        headerName: 'Milestone',
        minWidth: 200,
        resizable: true,
        tooltipField: 'name',
      },
      {
        cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
        cellDataType: 'text',
        field: 'amount',
        headerName: 'Payment ($)',
        minWidth: 120,
        valueFormatter: Utils.agCurrencyFormatter,
        valueSetter: (params) => agFormatToNumberValueSetter(params, ['$', ','], 'amount'),
      },
      {
        cellClass: TableConstants.STYLE_CLASSES.CELL_ALIGN_RIGHT,
        cellEditor: AgDatePickerComponent,
        comparator: (date1, date2) => {
          const date1Number = date1 ? dayjs(date1).unix() : null;
          const date2Number = date2 ? dayjs(date2).unix() : null;

          if (date1Number === null && date2Number === null) {
            return 0;
          }

          if (date1Number === null) {
            return 1;
          }

          if (date2Number === null) {
            return -1;
          }

          return date1Number - date2Number;
        },
        field: 'target_date',
        headerName: 'Target Date',
        minWidth: 150,
        sort: 'asc',
        valueFormatter: Utils.agDateFormatter,
      },
      {
        cellClass: 'text-left',
        cellEditor: 'agRichSelectCellEditor',
        cellEditorParams: () => {
          return {
            formatValue: (value?: Option | string) => {
              return isString(value) ? value : value?.label || '';
            },
            values: this.vendorOptions(),
          };
        },
        field: 'organization',
        filterValueGetter: (params: ValueGetterParams) => {
          return params.data?.organization?.name;
        },
        headerName: 'Vendor Contract',
        minWidth: 200,
        resizable: true,
        tooltipField: 'organization.name',
        valueGetter: (params: ValueGetterParams) => {
          return params.data?.organization?.name || '';
        },
        valueSetter: this.vendorValueSetter,
      },
    ],
    enableRangeSelection: true,
    enterNavigatesVertically: true,
    enterNavigatesVerticallyAfterEdit: true,
    fillOperation: (params: FillOperationParams) => {
      if (params.column.getColId() === 'target_date') {
        return params.values[0];
      }

      return false;
    },
    getRowId: ({ data }) => data?.id || data?.randomID,
    rowClassRules: {
      'has-error': (params) => params.data.showError,
    },
    suppressCellFocus: false,
    suppressMenuHide: true,
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 20,
  } as GridOptions;
  hasChanges = false;
  initialValues: PaymentMilestoneGridData[] = [];
  newRowAdded = false;
  removedRows: string[] = [];

  cancelEditMode(): void {
    this.gridAPI.stopEditing();
    this.editModeGrid$.next(false);
    AgSetColumnsVisible({
      gridApi: this.gridAPI,
      keys: [TableConstants.FIELDS.ACTIONS],
      visible: false,
    });
    this.sizeColumnsToFit();
    this.gridData$ = this.paymentMilestoneQuery.selectAllMilestones();
    this.resetChangeIndicators();
    this.gridAPI.redrawRows();
  }

  async canDeactivate(): Promise<boolean> {
    if (this.hasChanges) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  cellValueChanged(event: CellValueChangedEvent) {
    if (event.data.id) {
      this.editedRows.add(event.data.id);
      this.checkChanges();
    }
  }

  checkChanges() {
    const currentValues: PaymentMilestoneGridData[] = [];

    this.gridAPI?.forEachNode(({ data }) => {
      currentValues.push({ ...data, amount: Number(data.amount) });
    });

    this.hasChanges = !isEqual(this.initialValues, currentValues);
  }

  constructor() {
    combineLatest([this.paymentMilestoneService.get(), this.organizationService.get()])
      .pipe(takeUntilDestroyed())
      .subscribe();

    this.gridData$.pipe(takeUntilDestroyed()).subscribe((gridData) => {
      this.initialValues = gridData;
    });

    combineLatest([
      this.eventService.select$(EventType.TRIAL_CHANGED),
      this.mainQuery.select('trialKey'),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.initialValues = [];
        this.cancelEditMode();
      });
  }

  editGrid(): void {
    this.editModeGrid$.next(true);
    AgSetColumnsVisible({
      gridApi: this.gridAPI,
      keys: [TableConstants.FIELDS.ACTIONS],
      visible: true,
    });
    this.sizeColumnsToFit();
    this.gridAPI.redrawRows();
    this.gridAPI.startEditingCell({
      colKey: 'name',
      rowIndex: 0,
    });
  }

  gridReady({ api }: GridReadyEvent) {
    this.gridAPI$.next(api);
    this.gridAPI = api;
    this.sizeColumnsToFit();
    Utils.updateGridLayout(this.gridAPI, 'paymentMilestonesGrid');
  }

  onAddPaymentMilestone() {
    const ss = this.gridAPI.applyTransaction({
      add: [{ randomID: Utils.uuid() }],
      addIndex: this.gridAPI.getDisplayedRowCount(),
    });

    if (ss?.add.length && ss.add[0].rowIndex) {
      this.gridAPI.startEditingCell({ colKey: 'name', rowIndex: ss.add[0].rowIndex });
    }

    this.newRowAdded = true;
    this.checkChanges();
  }

  onSaveAll = async () => {
    if (this.showErrors()) {
      this.overlayService.error(MessagesConstants.RESOLVE_TABLE_ERRORS);

      return;
    }

    this.paymentMilestoneStore.setLoading(true);

    const upsertData: (CreatePaymentMilestoneInput & { id: null | string })[] = [];

    this.gridAPI.forEachNode((node) => {
      const { amount, id, name, organization, target_date } = node.data as RequireSome<
        PaymentMilestoneGridData,
        'amount' | 'id' | 'name' | 'organization' | 'target_date'
      >;

      if ((!id || this.editedRows.has(id)) && organization) {
        upsertData.push({
          amount,
          id,
          name,
          organization_id: organization.id,
          target_date,
        });
      }
    });

    let hasError = true;

    if (upsertData) {
      hasError = await this.paymentMilestoneService.upsert(upsertData);
    }

    if (this.removedRows.length) {
      hasError = await this.paymentMilestoneService.remove(this.removedRows);
    }

    if (!hasError) {
      this.overlayService.success();
    }

    this.cancelEditMode();
    this.paymentMilestoneStore.setLoading(false);
  };

  private resetChangeIndicators(): void {
    this.newRowAdded = false;
    this.editedRows.clear();
    this.removedRows = [];
    this.hasChanges = false;
  }

  showErrors() {
    const rowNodes: IRowNode[] = [];
    let isThereAnyInvalidRow = false;

    this.gridAPI.forEachNode((node) => {
      const { amount, name, organization, target_date } = node.data as PaymentMilestoneGridData;

      if (amount && name && organization && target_date) {
        if (node.data.showError) {
          node.data.showError = false;
          rowNodes.push(node);
        }
      } else {
        isThereAnyInvalidRow = true;
        node.data.showError = true;
        rowNodes.push(node);
      }
    });

    this.gridAPI.redrawRows({ rowNodes });

    return isThereAnyInvalidRow;
  }

  sizeColumnsToFit(): void {
    this.gridAPI.sizeColumnsToFit();
  }

  private vendorOptions() {
    return (
      this.organizationQuery
        .getAllVendors()
        .map(({ id, name }) => ({ label: name || '', value: id })) || []
    );
  }
}
